import dayjs from 'dayjs'
import { isArray, result, reduce, isUndefined, find, map, sortBy, flatten } from 'libraries/lodash'
import { addCommas } from 'helpers/transforms'
import { DISPLAY_DATE_FORMAT, DEAL_DAY_FORMAT } from '../../constants/locale'

const calculateStatsChange = (thisMonth, previousMonth) => {
  const operator = thisMonth >= previousMonth ? '+' : '-'
  return previousMonth === 0
    ? '+ ∞%'
    : operator + ' ' + Math.abs(100 * (thisMonth - previousMonth) / previousMonth).toFixed(0) + '%'
}

const formatStatsSummary = (title, thisMonth, previousMonth) => ({
  title,
  up: thisMonth > previousMonth,
  down: thisMonth < previousMonth,
  value: thisMonth,
  description: thisMonth === previousMonth ||
    previousMonth === 0
    ? ''
    : `${calculateStatsChange(thisMonth, previousMonth)} from previous month`
})

const reduceByKey = (path) => list => reduce(
  list,
  (acc, listItem) => result(listItem, path) ? { ...acc, [result(listItem, path)]: listItem } : acc,
  {}
)

class Parameter {
  constructor (param = {}) {
    Object.assign(this, param)
  }

  extend () {
    Object.assign(this, ...arguments)
    return this
  }

  static formatPrimitive = (config) => (x) => {
    const restrictingValues = result(config, 'config.restrictingValues', config.restrictingValues) || []
    const displayValue = find(restrictingValues, r => r.value === x.value)
    const format = result(config, 'config.format', config.format)

    switch (x.type) {
      case 'StringParameterValue':
        return displayValue ? displayValue.displayName : x.value
      case 'BooleanParameterValue':
        return x.value ? 'Yes' : 'No'
      case 'DateParameterValue':
        return dayjs(x.value).format(DISPLAY_DATE_FORMAT)
      case 'NumberParameterValue':
        if (format) {
          switch (format.type) {
            case 'Currency':
              return format.symbol + addCommas(x.value)
            case 'Percentage':
              return x.value + '%'
          }
        } else {
          return addCommas(x.value)
        }
    }
    return x.value
  }

  static comparePrimitive = (config) => (x) => {
    const restrictingValues = result(config, 'config.restrictingValues', config.restrictingValues) || []
    const displayValue = find(restrictingValues, r => r.value === x.value)

    switch (x.type) {
      case 'StringParameterValue':
        return displayValue ? displayValue.displayName : x.value
      case 'BooleanParameterValue':
        return x.value ? 2 : 1
      case 'DateParameterValue':
        return dayjs(x.value).unix()
      case 'NumberParameterValue':
        return +x.value
    }
    return x.value
  }

  get formattedValue () {
    try {
      const { config } = this
      const convert = Parameter.formatPrimitive(config)
      const { value, values, minValue, maxValue } = this
      if (!isUndefined(value) || values || maxValue || minValue) {
        switch (this.type) {
          case 'RangedParameterValue':
            if (minValue && maxValue) {
              return `${convert(minValue)} - ${convert(maxValue)}`
            } else {
              return convert(minValue || maxValue)
            }
          case 'MultiSelectParameterValue':
            if (config.restrictingValues) {
              return values.map(x => config.restrictingValues.find(y => x.value === y.value).displayName).join('; ')
            } else {
              return values.map(convert).join('; ')
            }
          default:
            return convert(this)
        }
      }
    } catch (e) {
      console.log('error', e)
      return 'formatting error'
    }
  }

  get comparableValue () {
    try {
      const { config } = this
      const convert = Parameter.comparePrimitive(config)
      const { value, values, minValue, maxValue } = this
      if (!isUndefined(value) || value === false || values || maxValue || minValue) {
        switch (this.type) {
          case 'RangedParameterValue':
            return convert(minValue || maxValue)
          case 'MultiSelectParameterValue':
            if (config.restrictingValues) {
              return values.map(x => config.restrictingValues.find(y => x.value === y.value).displayName).join('; ')
            } else {
              return values.map(convert).join('; ')
            }
          default:
            return convert(this)
        }
      }
    } catch (e) {
      return ''
    }
  }

  get () {
    return result(this, ...arguments)
  }

  static create (param) {
    return new Parameter(param)
  }
}

export class Deal {
  constructor (param) {
    Object.assign(this, param, { timeline: this.convertTimeline(param.timeline) })
  }

  convertTimeline (timeline) {
    const timelineMapped = map(
      timeline,
      te => te.type === 'MultiSelectParameterValue'
        ? te.values.map(({ descriptor, type, value }) => ({
          ...te,
          type,
          value,
          descriptor: {
            ...te.descriptor,
            ...descriptor
          }
        }))
        : te
    )
    return sortBy(
      flatten(timelineMapped),
      'value'
    )
  }

  getParam (name) {
    return (this.params && this.params[name]) || new Parameter()
  }
}

const mapNonsecuredEventToDeal = ({
  descriptor: {
    uuid: dealId,
    eventType,
    description: title,
    id
  },
  primaryDate,
  timeline
}) => new Deal({
  id: `${eventType.toUpperCase()}-${id}`,
  dealId,
  dealType: eventType,
  title,
  primaryDate: dayjs(primaryDate),
  timeline
})

const mapEventToDeal = ({
  descriptor: {
    uuid: dealId,
    eventType,
    description: title,
    id
  },
  comment: expertCommentary,
  documents,
  parameterComments,
  primaryDate,
  relatedEvents,
  timeline,
  values,
  score
}) => new Deal({
  id: `${eventType.toUpperCase()}-${id}`,
  dealId,
  dealType: eventType,
  title,
  primaryDate: dayjs(primaryDate),
  expertCommentary,
  relatedEvents: reduceByKey('uuid')(relatedEvents),
  params: reduceByKey('descriptor.name')(values.map(Parameter.create)),
  documents: reduceByKey('id')(documents),
  timeline,
  comments: reduceByKey('parameterName')(parameterComments),
  score
})

export const toEventTypes = response => {
  const { data } = JSON.parse(response)

  return data.reduce((acc, { type, displayName: name, prefix }) => ({
    ...acc, [type]: { type, name, prefix }
  }), {})
}

export const toFeaturedDeal = response => {
  const { data } = JSON.parse(response)
  const deal = isArray(data) ? data[0] : data
  if (!deal) {
    return null
  }
  const {
    comment: dealCommentary,
    dateFeatured,
    descriptor: {
      description: dealName,
      eventType: dealType,
      uuid: dealId
    }
  } = deal
  const dealDate = dayjs(dateFeatured).format(DEAL_DAY_FORMAT)

  return {
    dealDate,
    dealCommentary,
    dealName,
    dealType,
    dealId
  }
}

export const toStatsSummary = response => {
  const { data: { avgDealSize, ipo, pma, so } } = JSON.parse(response)
  const deals = [
    formatStatsSummary('No. of Public M&A Deals', pma.thisMonth, pma.previousMonth),
    formatStatsSummary('No. of IPOs', ipo.thisMonth, ipo.previousMonth),
    formatStatsSummary('No. of Secondary Offers', so.thisMonth, so.previousMonth)
  ]
  if (avgDealSize) {
    deals.push({
      title: 'Average Deal Size',
      up: avgDealSize.previousMonth
        ? avgDealSize.thisMonth > avgDealSize.previousMonth
        : null,
      down: avgDealSize.previousMonth
        ? avgDealSize.thisMonth < avgDealSize.previousMonth
        : null,
      value: '£' + addCommas(Math.round(avgDealSize.thisMonth)),
      description: avgDealSize.previousMonth
        ? `${calculateStatsChange(avgDealSize.thisMonth, avgDealSize.previousMonth)} from previous month`
        : ''
    })
  }
  return deals
}

export const toLatestDeals = response => {
  const { data } = JSON.parse(response)
  return { results: data.map(mapNonsecuredEventToDeal) }
}

export const toEventConfiguration = response => {
  const { data } = JSON.parse(response)
  const { eventType, eventDate } = data
  const parameters = data.parameters.reduce(
    (a, p, index) => ({
      ...a,
      [p.name]: { index, ...p }
    }),
    {}
  )

  const documentConfigs = data.documentConfigs.reduce(
    (acc, { documentType: { displayName, type }, name }) =>
      ({ ...acc, [type]: { name, displayName, type } }),
    {}
  )

  return {
    parameters,
    eventDate,
    eventType,
    documentConfigs
  }
}

export const toSharedEventsConfiguration = response => {
  const { data } = JSON.parse(response)
  const parameters = data.reduce(
    (a, p) => ({
      ...a,
      [p.name]: {
        ...result(p, ['childParameters', 0], {}),
        ...p,
        filterGroup: { type: 'Basic' }
      }
    }),
    {}
  )

  return {
    all: { parameters }
  }
}

export const toListDeals = response => {
  const { data: { total, pageNumber, data } } = JSON.parse(response)
  return {
    results: data.map(mapNonsecuredEventToDeal),
    total,
    pageNumber
  }
}

export const toStats = response => {
  const { data } = JSON.parse(response)
  return data
}

export const toDeal = response => {
  const { data } = JSON.parse(response)
  return mapEventToDeal(data)
}

export const toDealDocument = async response => {
  const buffer = await response.arrayBuffer()
  return { buffer }
}
